import { Button } from "@mantine/core";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { MissingObjectDataProps } from "../../../hooks/useShouldFieldBeHighlighted";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import { applyHighlightingToObjectFormFieldData } from "../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { FormSection } from "../../BuildingBlocks/Forms/FormSection/FormSection";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { useMeteringConceptMeterMutation } from "../hooks/useMeteringConceptMeterMutation";
import type {
  MeteringConceptMeterFieldsResponse,
  MeterUpdateRequest
} from "../Meter.types";
import { MESSKONZEPT_FORM_FIELD_DATA } from "./MesskonzeptForm.constants";
import type { MesskonzeptFormData } from "./MesskonzeptForm.types";

type MesskonzeptFormProps = {
  meterId: number;
  meteringConceptData: MeteringConceptMeterFieldsResponse;
  siteId: number;
  onChangeNumErrors: (numErrors: number) => void;
  onClose: () => void;
} & MissingObjectDataProps;

function MesskonzeptForm({
  fieldsThatCouldBeMissing,
  meterId,
  meteringConceptData,
  shouldFieldBeHighlighted,
  siteId,
  onChangeNumErrors,
  onClose
}: MesskonzeptFormProps) {
  const { showToast, showErrorToast } = useToast();
  const { t } = useTranslation();
  const { editMutation } = useMeteringConceptMeterMutation(meterId, siteId);
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    watch
  } = useForm<MesskonzeptFormData>({
    defaultValues: getDefaultValues(meteringConceptData)
  });
  // A non-field server error leaves RHF's form errors empty, so its own
  // isSubmitSuccessful would report the failed submit as a success.
  const [hasSubmitSucceeded, setHasSubmitSucceeded] = useState(false);

  // The per-field highlighting (orange circle next to a missing field) is
  // computed here at render time. The tab-level highlight is derived by the
  // parent wizard instead — see the comment in MeterWizard.
  const { formFieldData } = applyHighlightingToObjectFormFieldData(
    MESSKONZEPT_FORM_FIELD_DATA,
    watch,
    {
      wizardObject: meteringConceptData,
      fieldsThatCouldBeMissing,
      shouldFieldBeHighlighted
    }
  );

  useEffect(() => {
    onChangeNumErrors(Object.keys(errors).length);
  }, [errors, onChangeNumErrors]);

  const onValidSubmit: SubmitHandler<MesskonzeptFormData> = async (data) => {
    setHasSubmitSucceeded(false);

    try {
      await editMutation.mutateAsync({
        ...getBaseMeterFields(meteringConceptData),
        ...data
      });
      setHasSubmitSucceeded(true);
      showToast({
        type: "success",
        message: "Die Änderungen wurden gespeichert."
      });
      onClose();
    } catch (error) {
      setErrorsFromResponseData<MesskonzeptFormData>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  return (
    <form className="MesskonzeptForm" onSubmit={submitCallback}>
      <FormSection
        defaultOpen
        isCollapsible={false}
        label="(technische) Zähler Daten (Premium)"
        mt="md"
      >
        <FormFieldController
          control={control}
          data={formFieldData.measurement_description}
        />
        <FormFieldController
          control={control}
          data={formFieldData.installation_location}
        />
        <FormFieldController control={control} data={formFieldData.voltage} />
        <FormFieldController
          control={control}
          data={formFieldData.yearly_consumption}
        />
        <FormFieldController
          control={control}
          data={formFieldData.tariff_type}
        />
        <FormFieldController
          control={control}
          data={formFieldData.meter_billing_relevant_or_submeter}
        />
        <FormFieldController
          control={control}
          data={formFieldData.aufhaengung}
        />
        <FormFieldController
          control={control}
          data={formFieldData.measuring_method}
        />
        <FormFieldController
          control={control}
          data={formFieldData.absicherung_in_ampere}
        />
        <FormFieldController
          control={control}
          data={formFieldData.installation_date}
        />
      </FormSection>

      <ModalFooter>
        <Button
          disabled={isSubmitting || hasSubmitSucceeded}
          variant="default"
          onClick={onClose}
        >
          Abbrechen
        </Button>
        <Button
          color="secondary"
          loading={isSubmitting || hasSubmitSucceeded}
          onClick={submitCallback}
        >
          Speichern
        </Button>
      </ModalFooter>
    </form>
  );
}

function getBaseMeterFields(
  meteringConceptData: MeteringConceptMeterFieldsResponse
): MeterUpdateRequest {
  return {
    id: meteringConceptData.id,
    global_id: meteringConceptData.global_id,
    site: meteringConceptData.site,
    name: meteringConceptData.name,
    number: meteringConceptData.number,
    medium: meteringConceptData.medium,
    frequency: meteringConceptData.frequency,
    measurement_type: meteringConceptData.measurement_type,
    metering_direction: meteringConceptData.metering_direction,
    location_feedin: meteringConceptData.location_feedin,
    location_feedout: meteringConceptData.location_feedout,
    sub_metering_system: meteringConceptData.sub_metering_system,
    is_calibrated: meteringConceptData.is_calibrated,
    calibration_valid_until: meteringConceptData.calibration_valid_until,
    conversion_factor: meteringConceptData.conversion_factor
  };
}

function getDefaultValues(
  meteringConceptData: MeteringConceptMeterFieldsResponse
): MesskonzeptFormData {
  return {
    measurement_description:
      meteringConceptData.measurement_description ?? null,
    measuring_method: meteringConceptData.measuring_method ?? null,
    voltage: meteringConceptData.voltage ?? null,
    installation_location: meteringConceptData.installation_location ?? null,
    installation_date: meteringConceptData.installation_date ?? null,
    yearly_consumption: meteringConceptData.yearly_consumption ?? null,
    tariff_type: meteringConceptData.tariff_type ?? null,
    meter_billing_relevant_or_submeter:
      meteringConceptData.meter_billing_relevant_or_submeter ?? null,
    aufhaengung: meteringConceptData.aufhaengung ?? null,
    absicherung_in_ampere: meteringConceptData.absicherung_in_ampere ?? null
  };
}

export { MesskonzeptForm, MesskonzeptFormProps };
